import { useState, useEffect, useCallback } from 'react';
import { Mountain, TrendingUp, TrendingDown, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Gradient severity thresholds (in %)
const GRADIENT_MILD = 5;      // 5% — noticeable but easy
const GRADIENT_MODERATE = 8;  // 8% — moderate hill
const GRADIENT_STEEP = 12;    // 12% — steep hill
const GRADIENT_SEVERE = 15;   // 15% — very steep

function getGradientColor(gradientPct) {
  const abs = Math.abs(gradientPct);
  if (abs < GRADIENT_MILD) return '#22c55e';     // green
  if (abs < GRADIENT_MODERATE) return '#eab308';  // yellow
  if (abs < GRADIENT_STEEP) return '#f97316';     // orange
  return '#ef4444';                                // red
}

function getGradientLabel(gradientPct) {
  const abs = Math.abs(gradientPct);
  if (abs < GRADIENT_MILD) return 'Flat';
  if (abs < GRADIENT_MODERATE) return 'Gentle';
  if (abs < GRADIENT_STEEP) return 'Moderate';
  if (abs < GRADIENT_SEVERE) return 'Steep';
  return 'Very Steep';
}

function analyzeSteepSections(elevations, routeCoords) {
  if (!elevations || elevations.length < 2 || !routeCoords) return [];
  const sections = [];
  const step = Math.max(1, Math.floor(routeCoords.length / elevations.length));

  for (let i = 1; i < elevations.length; i++) {
    const rise = elevations[i] - elevations[i - 1];
    // Approximate run distance using coordinates
    const idx1 = Math.min((i - 1) * step, routeCoords.length - 1);
    const idx2 = Math.min(i * step, routeCoords.length - 1);
    const c1 = routeCoords[idx1];
    const c2 = routeCoords[idx2];
    const lat1 = c1.lat || c1[0];
    const lng1 = c1.lng || c1[1];
    const lat2 = c2.lat || c2[0];
    const lng2 = c2.lng || c2[1];

    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    const run = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    if (run < 10) continue; // skip very short segments

    const gradientPct = (rise / run) * 100;

    if (Math.abs(gradientPct) >= GRADIENT_MODERATE) {
      sections.push({
        index: i,
        from: { lat: lat1, lng: lng1, elev: elevations[i - 1] },
        to: { lat: lat2, lng: lng2, elev: elevations[i] },
        rise: Math.round(rise),
        run: Math.round(run),
        gradient: Math.round(gradientPct * 10) / 10,
        severity: getGradientLabel(gradientPct),
        isUphill: rise > 0,
      });
    }
  }
  return sections;
}

export const ElevationProfile = ({ routeCoords, c1, c2, onHillWarnings }) => {
  const [elevations, setElevations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [steepSections, setSteepSections] = useState([]);

  const processElevations = useCallback((elevData) => {
    setElevations(elevData);
    const sections = analyzeSteepSections(elevData, routeCoords);
    setSteepSections(sections);
    if (onHillWarnings) onHillWarnings(sections);
  }, [routeCoords, onHillWarnings]);

  useEffect(() => {
    if (!routeCoords || routeCoords.length < 2) { setElevations(null); setSteepSections([]); return; }
    const step = Math.max(1, Math.floor(routeCoords.length / 50));
    const sampled = routeCoords.filter((_, i) => i % step === 0 || i === routeCoords.length - 1);
    const lats = sampled.map(c => (c.lat || c[0]).toFixed(4)).join(',');
    const lngs = sampled.map(c => (c.lng || c[1]).toFixed(4)).join(',');

    setLoading(true);
    fetch(`${API}/api/offline-nav/elevation?lats=${lats}&lngs=${lngs}`)
      .then(r => r.json())
      .then(data => {
        if (data.elevations) {
          try { localStorage.setItem('crisis_elevation_cache', JSON.stringify({ lats, lngs, elevations: data.elevations })); } catch {}
          processElevations(data.elevations);
        }
      })
      .catch(() => {
        try {
          const cached = JSON.parse(localStorage.getItem('crisis_elevation_cache') || 'null');
          if (cached?.elevations) processElevations(cached.elevations);
        } catch {}
      })
      .finally(() => setLoading(false));
  }, [routeCoords, processElevations]);

  if (!elevations || elevations.length < 2) return null;

  const maxElev = Math.max(...elevations);
  const minElev = Math.min(...elevations);
  const totalClimb = elevations.reduce((acc, e, i) => i > 0 && e > elevations[i - 1] ? acc + (e - elevations[i - 1]) : acc, 0);
  const totalDescent = elevations.reduce((acc, e, i) => i > 0 && e < elevations[i - 1] ? acc + (elevations[i - 1] - e) : acc, 0);
  const hasSteepHills = steepSections.some(s => s.severity === 'Steep' || s.severity === 'Very Steep');
  const hasModerateHills = steepSections.length > 0;

  // SVG with gradient-colored segments
  const w = 280, h = 50;
  const range = maxElev - minElev || 1;
  const pts = elevations.map((e, i) => ({
    x: (i / (elevations.length - 1)) * w,
    y: h - ((e - minElev) / range) * h,
  }));
  const fillPoints = `0,${h} ${pts.map(p => `${p.x},${p.y}`).join(' ')} ${w},${h}`;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2.5 space-y-1.5" data-testid="elevation-profile">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 text-xs"
        data-testid="elevation-toggle"
      >
        <Mountain className="w-3.5 h-3.5" style={{ color: c1 }} />
        <span className="font-semibold text-gray-800">Elevation Profile</span>
        {hasSteepHills && (
          <span className="flex items-center gap-1 text-red-400 text-[10px] ml-auto mr-1">
            <AlertTriangle className="w-3 h-3" /> Steep hills
          </span>
        )}
        {!hasSteepHills && hasModerateHills && (
          <span className="flex items-center gap-1 text-amber-400 text-[10px] ml-auto mr-1">
            <AlertTriangle className="w-3 h-3" /> Hills
          </span>
        )}
        {expanded ? <ChevronUp className="w-3 h-3 text-muted-foreground" /> : <ChevronDown className="w-3 h-3 text-muted-foreground" />}
      </button>

      {/* Elevation chart with gradient-colored segments */}
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-10" preserveAspectRatio="none">
        <defs>
          <linearGradient id="elev-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c1} stopOpacity="0.4" />
            <stop offset="100%" stopColor={c1} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <polygon points={fillPoints} fill="url(#elev-grad)" />
        {/* Draw segments with gradient-based colors */}
        {pts.map((p, i) => {
          if (i === 0) return null;
          const prev = pts[i - 1];
          const rise = elevations[i] - elevations[i - 1];
          const segLen = Math.sqrt((p.x - prev.x) ** 2 + (p.y - prev.y) ** 2) || 1;
          const gradPct = (rise / (segLen * 10)) * 100; // approximate
          const color = getGradientColor(gradPct * 3); // amplify for visibility
          return (
            <line
              key={i}
              x1={prev.x} y1={prev.y}
              x2={p.x} y2={p.y}
              stroke={color}
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
      </svg>

      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{Math.round(minElev)}m</span>
        <div className="flex gap-3">
          <span className="flex items-center gap-0.5"><TrendingUp className="w-2.5 h-2.5 text-green-400" />{Math.round(totalClimb)}m</span>
          <span className="flex items-center gap-0.5"><TrendingDown className="w-2.5 h-2.5 text-red-400" />{Math.round(totalDescent)}m</span>
        </div>
        <span>{Math.round(maxElev)}m</span>
      </div>

      {/* Expanded: Steep sections detail */}
      {expanded && steepSections.length > 0 && (
        <div className="space-y-1 pt-1 border-t border-border/20" data-testid="steep-sections">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Hill Warnings</p>
          {steepSections.map((sec, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-1 rounded-md text-[10px]" style={{ backgroundColor: `${getGradientColor(sec.gradient)}15`, borderLeft: `3px solid ${getGradientColor(sec.gradient)}` }}>
              {sec.isUphill ? (
                <TrendingUp className="w-3 h-3 flex-shrink-0" style={{ color: getGradientColor(sec.gradient) }} />
              ) : (
                <TrendingDown className="w-3 h-3 flex-shrink-0" style={{ color: getGradientColor(sec.gradient) }} />
              )}
              <span className="text-foreground font-medium">
                {sec.severity} {sec.isUphill ? 'climb' : 'descent'}
              </span>
              <span className="text-muted-foreground">
                {Math.abs(sec.gradient)}% over {sec.run}m ({sec.isUphill ? '+' : ''}{sec.rise}m)
              </span>
            </div>
          ))}
          {/* Gradient legend */}
          <div className="flex gap-2 pt-1 text-[9px] text-muted-foreground">
            <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#22c55e' }} />Flat</span>
            <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#eab308' }} />Gentle</span>
            <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#f97316' }} />Moderate</span>
            <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#ef4444' }} />Steep</span>
          </div>
        </div>
      )}

      {expanded && steepSections.length === 0 && (
        <div className="text-[10px] text-muted-foreground text-center py-1 border-t border-border/20">
          No significant hills on this route
        </div>
      )}
    </div>
  );
};
